export type SalaryPeriod = "year" | "month"

export type SalaryFields = {
  salaryMin?: number
  salaryMax?: number
  salaryPeriod?: SalaryPeriod
  salaryRange?: string
}

function formatINR(n: number) {
  return n.toLocaleString("en-IN")
}

function toLPA(n: number) {
  return (n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)
}

const LPA_MULTIPLIER = 100_000

/** Form displays LPA for year period; Convex stores absolute rupees. */
export function lpaToRupees(lpa: number) {
  return lpa * LPA_MULTIPLIER
}

export function rupeesToLpaStr(rupees: number | undefined) {
  if (rupees === undefined) return ""
  const lpa = rupees / LPA_MULTIPLIER
  return Number.isInteger(lpa) ? String(lpa) : lpa.toFixed(1)
}

/**
 * Prefers the structured salaryMin/Max/Period over the legacy free-text
 * salaryRange — see docs/wellfound/recruit-platform/schema-proposal.md §3.3.
 */
export function formatSalary(job: SalaryFields): string | null {
  const { salaryMin: min, salaryMax: max, salaryPeriod: period } = job
  if (min !== undefined || max !== undefined) {
    const fmt = period === "month" ? formatINR : toLPA
    const suffix = period === "month" ? "/mo" : " LPA"
    if (min !== undefined && max !== undefined && min !== max) {
      return `₹${fmt(min)}–${fmt(max)}${suffix}`
    }
    return `₹${fmt(min ?? max!)}${suffix}`
  }
  return job.salaryRange?.trim() || null
}

/** Server-side legacy fallback written alongside structured fields. */
export function deriveSalaryRange(args: {
  salaryMin?: number
  salaryMax?: number
  salaryPeriod?: SalaryPeriod
  unpaid?: boolean
}): string | undefined {
  if (args.unpaid) return "Unpaid"
  const formatted = formatSalary(args)
  return formatted ?? undefined
}

export function jobHasPaidCompensation(job: SalaryFields): boolean {
  if (job.salaryMin !== undefined || job.salaryMax !== undefined) return true
  const legacy = job.salaryRange?.trim()
  if (!legacy) return false
  return !/^unpaid$/i.test(legacy)
}

export function jobIsUnpaid(job: SalaryFields): boolean {
  if (job.salaryMin !== undefined || job.salaryMax !== undefined) return false
  return /^unpaid$/i.test(job.salaryRange?.trim() ?? "")
}
